import React, {useState, useEffect} from 'react';
import styled from 'styled-components';
import Chart from 'react-apexcharts';
import ApexCharts from 'apexcharts';

import StyledPaper from '../Paper';
import StyledPaperTitle from '../PaperTitle';
import DateSelectorButton from '../DateSelectorButton';
import {useSelector} from 'react-redux';
import {mergeApexTooltipOptions} from 'utils/apexTooltip';

const StyledApexLineChart = styled(StyledPaper)`
  .apexcharts-tooltip {
    background: ${props => props.theme.tableRow};
    border: 1px solid ${props => props.theme.text};
    color: ${props => props.theme.text};

    .apexcharts-tooltip-title {
      background: ${props => props.theme.tableRow};
    }
  }

  height: calc(100% - 48px);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-left: 24px;
  padding-right: 24px;
`;

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

function ApexLineChart(props) {
  const {
    children,
    data,
    loaded,
    className,
    title,
    colors,
    yAxisTitle,
    yAxisMin,
    yAxisMax,
    selectedTime,
    setSelectedTimeFn,
  } = props;
  const chartTitle = title || 'Trending Events';
  const yAxisTitleText = yAxisTitle || 'Number of Events';

  const theme = useSelector(state => state.theme);
  const themeSelected = theme[theme.select];
  const themeColors = theme[theme.select].altColors;

  const [chartOptions] = useState({
    options: {
      colors: colors || themeColors,
      chart: {
        id: 'line-datetime',
        height: 200,
        type: 'line',
        toolbar: {
          show: false,
        },
        zoom: {
          autoScaleYaxis: false,
        },
        dropShadow: {
          enabled: false,
          enabledOnSeries: undefined,
          top: 0,
          left: 0,
          blur: 1,
          color: themeSelected.text,
          opacity: 0.55,
        },
      },
      noData: {
        text: 'Loading...',
      },
      dataLabels: {
        enabled: false,
      },
      legend: {
        height: 60,
        showForSingleSeries: true,
        labels: {
          // colors: themeColors,
          useSeriesColors: true,
        },
        markers: {
          width: 12,
          height: 12,
          strokeWidth: 0,
          strokeColor: themeSelected.text,
          // fillColors: themeColors,
          radius: 12,
          customHTML: undefined,
          onClick: undefined,
          offsetX: 0,
          offsetY: 0,
        },
        itemMargin: {
          horizontal: 16,
        },
      },
      markers: {
        size: 0,
        // colors: themeColors,
        strokeColors: themeSelected.text,
        strokeWidth: 2,
        strokeOpacity: 0.45,
        strokeDashArray: 0,
        fillOpacity: 1,
        discrete: [],
        shape: 'circle',
        radius: 5,
        offsetX: 0,
        offsetY: 0,
        onClick: undefined,
        onDblClick: undefined,
        showNullDataPoints: true,
        hover: {
          size: undefined,
          sizeOffset: 7,
        },
      },
      title: {
        align: 'left',
      },
      grid: {
        borderColor: themeSelected.secondary,
        row: {
          colors: ['transparent', 'transparent'], // takes an array which will be repeated on columns
          opacity: 0.5,
        },
      },
      xaxis: {
        type: 'datetime',
        min: new Date(new Date().getTime() - 7 * 24 * 60 * 60 * 1000).getTime(),
        max: new Date().getTime(),
        labels: {
          style: {
            colors: themeSelected.text,
            fontSize: '12px',
            fontFamily: 'Helvetica, Arial, sans-serif',
            fontWeight: 400,
            cssClass: 'apexcharts-xaxis-label',
          },
        },
        tooltip: {
          enabled: false,
        },
      },
      yaxis: {
        ...(yAxisMin != null ? {min: yAxisMin} : {}),
        ...(yAxisMax != null ? {max: yAxisMax} : {}),
        decimalsInFloat: 0,
        labels: {
          show: true,
          style: {
            colors: themeSelected.text,
            fontSize: '12px',
            fontFamily: 'Helvetica, Arial, sans-serif',
            fontWeight: 400,
            cssClass: 'apexcharts-yaxis-label',
            formatter: function (val) {
              return parseInt(val) === val ? val : '';
            },
          },
          formatter: value => {
            return Math.floor(value);
          },
        },
        title: {
          text: yAxisTitleText,
          style: {
            color: themeSelected.text,
          },
        },
      },
      tooltip: mergeApexTooltipOptions(themeSelected, {
        shared: true,
        followCursor: true,
        colors: themeColors,
      }),
      annotations: {
        position: 'front',
      },
      stroke: {
        show: true,
        curve: 'straight',
        lineCap: 'butt',
        // colors: themeColors,
        width: 3,
        dashArray: 0,
      },
    },
  });

  // Selected Date Range
  const [select, setSelect] = useState(selectedTime || 'one_week');
  const [chartData, setChartData] = useState([
    {
      name: '',
      data: [0, 0],
    },
  ]);

  const updateData = React.useCallback(
    timeline => {
      const date = new Date();
      setSelect(timeline);
      if (setSelectedTimeFn) setSelectedTimeFn(timeline);

      switch (timeline) {
        case 'today':
          ApexCharts.exec(
            'line-datetime',
            'zoomX',
            new Date(date.getTime() - 1 * 24 * 60 * 60 * 1000).getTime(),
            new Date().getTime()
          );
          break;
        case 'one_week':
          ApexCharts.exec(
            'line-datetime',
            'zoomX',
            new Date(date.getTime() - 7 * 24 * 60 * 60 * 1000).getTime(),
            new Date().getTime()
          );
          break;
        case 'two_weeks':
          ApexCharts.exec(
            'line-datetime',
            'zoomX',
            new Date(date.getTime() - 14 * 24 * 60 * 60 * 1000).getTime(),
            new Date().getTime()
          );
          break;
        case 'one_month':
          ApexCharts.exec(
            'line-datetime',
            'zoomX',
            new Date(date.getTime() - 30 * 24 * 60 * 60 * 1000).getTime(),
            new Date().getTime()
          );
          break;
        case 'three_months':
          ApexCharts.exec(
            'line-datetime',
            'zoomX',
            new Date(date.getTime() - 90 * 24 * 60 * 60 * 1000).getTime(),
            new Date().getTime()
          );
          break;
        case 'one_year':
          ApexCharts.exec(
            'line-datetime',
            'zoomX',
            new Date(date.getTime() - 365 * 24 * 60 * 60 * 1000).getTime(),
            new Date().getTime()
          );
          break;
        default:
      }
    },
    [setSelectedTimeFn]
  );

  useEffect(() => {
    if (!loaded || !data) {
      return;
    }
    setChartData(data);
    ApexCharts.exec('line-datetime', 'updateSeries', data);
    updateData(select);
  }, [loaded, data, select, updateData]);

  useEffect(() => {
    ApexCharts.exec(
      'line-datetime',
      'updateOptions',
      {
        colors: colors || themeColors,
        yaxis: {
          ...chartOptions.options.yaxis,
          min: yAxisMin != null ? yAxisMin : undefined,
          max: yAxisMax != null ? yAxisMax : undefined,
          title: {
            text: yAxisTitleText,
            style: {
              color: themeSelected.text,
            },
          },
        },
      },
      false,
      true
    );
  }, [
    colors,
    themeColors,
    yAxisTitleText,
    yAxisMin,
    yAxisMax,
    chartOptions.options.yaxis,
    themeSelected.text,
  ]);

  return (
    <StyledApexLineChart id="chart">
      <StyledPaperTitle>{chartTitle}</StyledPaperTitle>
      <StyledButtonContainer>
        {/* <DateSelectorButton selected={select} value={'today'} onClick={() => updateData('today')}>Today</DateSelectorButton> */}
        <DateSelectorButton
          selected={select}
          value={'one_week'}
          onClick={() => updateData('one_week')}
        >
          1w
        </DateSelectorButton>
        <DateSelectorButton
          selected={select}
          value={'two_weeks'}
          onClick={() => updateData('two_weeks')}
        >
          2w
        </DateSelectorButton>
        <DateSelectorButton
          selected={select}
          value={'one_month'}
          onClick={() => updateData('one_month')}
        >
          1m
        </DateSelectorButton>
        <DateSelectorButton
          selected={select}
          value={'three_months'}
          onClick={() => updateData('three_months')}
        >
          3m
        </DateSelectorButton>
        <DateSelectorButton
          selected={select}
          value={'one_year'}
          onClick={() => updateData('one_year')}
          className={'disabled'}
        >
          1y
        </DateSelectorButton>
      </StyledButtonContainer>
      <Chart
        options={chartOptions.options}
        series={chartData}
        height={250}
        type="line"
        className={className}
      />
      {children}
    </StyledApexLineChart>
  );
}

export default ApexLineChart;
